import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Swords, Trophy, Info, X, RefreshCw } from "lucide-react";
import { spaceApi } from "./spaceApi";
import { SpaceHomeBtn } from "./SpaceNav";
import ArenaBattleCanvas from "./ArenaBattleCanvas";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FACTION = {
  Void: "#a78bfa", Bio: "#37e0a0", Plasma: "#00d5ff", Mech: "#4aa3ff",
  Recon: "#38bdf8", Tech: "#22d3ee", Shadow: "#8b5cf6", Cosmic: "#f5c542", Cyber: "#c084fc",
};
const TIER = { S: "#f5c542", A: "#c084fc", R: "#4aa3ff" };
const img = (p) => (p ? (p.startsWith("http") ? p : BACKEND + p) : null);
const fmt = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1000 ? `${(n / 1000).toFixed(1)}K` : `${n}`);
const PAD_X = ["18%", "34%", "50%", "66%", "82%"];

// Standalone PvP Challenge Arena — your locked squad (green pads, bottom) vs a
// matchmade near-power opponent (red pads, top). Instant resolve by squad power
// + synergy. SEPARATE from the Conquest world-map troop war.
export default function PvpArena({ onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState("plan"); // plan | fighting
  const [hpW, setHpW] = useState(100);
  const [result, setResult] = useState(null);
  const [details, setDetails] = useState(false);

  useEffect(() => {
    if (phase === "fighting") { setHpW(100); const t = setTimeout(() => setHpW(6), 80); return () => clearTimeout(t); }
    setHpW(100);
  }, [phase]);

  const load = async () => {
    setLoading(true); setResult(null);
    try { setData(await spaceApi.arenaOpponent()); }
    catch (e) { toast.error("Could not find an opponent"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startFight = () => { if (phase === "fighting") return; setResult(null); setPhase("fighting"); };
  const resolveFight = async () => {
    try {
      const r = await spaceApi.arenaChallenge();
      setResult(r);
      toast[r.win ? "success" : "error"](r.win ? `Victory! +${fmt(r.rewards.titanium)} titanium · +${r.rewards.trophies} 🏆` : "Defeated — try a new squad combo");
    } catch (e) { toast.error("Battle failed"); }
    finally { setPhase("plan"); }
  };

  if (loading || !data) {
    return <div className="ss-viewport grid place-items-center" data-testid="pvp-arena-loading"><Loader2 className="animate-spin text-[#ff3b4e]" /></div>;
  }

  const me = data.me, opp = data.opponent;
  const Pads = ({ heroes, color, testPrefix }) => (
    <div className="flex justify-around items-end px-2 gap-1">
      {PAD_X.map((_, i) => {
        const h = heroes[i];
        const fcol = h ? (FACTION[h.faction] || color) : color;
        return (
          <div key={i} data-testid={`${testPrefix}-pad-${i}`} className="flex flex-col items-center" style={{ width: "18%" }}>
            {h ? (
              <div className="w-full rounded-md overflow-hidden border-2 mb-1" style={{ borderColor: TIER[h.rarity_tier] || color, boxShadow: `0 0 8px ${fcol}` }}>
                {img(h.image_path) ? <img src={img(h.image_path)} alt={h.name} className="w-full object-cover" style={{ aspectRatio: "3/4" }} /> : <div className="grid place-items-center bg-black/60 font-mono text-sm font-black" style={{ aspectRatio: "3/4", color: fcol }}>{h.name?.[0]}</div>}
              </div>
            ) : (
              <div className="mb-1 font-mono text-[7px] uppercase text-white/30" style={{ height: "10px" }}>empty</div>
            )}
            <div className="w-full" style={{ aspectRatio: "2.2/1" }}>
              <div className="w-full h-full rounded-[50%]" style={{ border: `2px solid ${fcol}`, boxShadow: `0 0 10px ${fcol}`, background: `${fcol}1f` }} />
            </div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="ss-viewport relative flex flex-col" data-testid="pvp-arena" style={{ background: "#02060a" }}>
      <SpaceHomeBtn onClick={onBack} testid="pvp-arena-back" />

      {/* Power banner: mine (green) vs opponent (red) */}
      <div className="shrink-0 pt-12 px-4 flex items-stretch justify-between gap-2">
        <div className="flex-1 text-left" data-testid="pvp-arena-my-power">
          <p className="font-mono text-[8px] uppercase tracking-widest text-[#39ff7a]/80">Your Squad</p>
          <p className="font-mono text-2xl font-black tabular-nums" style={{ color: "#39ff7a", textShadow: "0 0 12px rgba(57,255,122,0.5)" }}>{fmt(me.power)}</p>
        </div>
        <div className="self-center px-2 font-mono text-[11px] font-black text-white/50">VS</div>
        <div className="flex-1 text-right" data-testid="pvp-arena-opp-power">
          <p className="font-mono text-[8px] uppercase tracking-widest text-[#ff3b4e]/80 truncate">{opp.name}</p>
          <p className="font-mono text-2xl font-black tabular-nums" style={{ color: "#ff3b4e", textShadow: "0 0 12px rgba(255,59,78,0.5)" }}>{fmt(opp.power)}</p>
        </div>
      </div>

      {/* Enemy health bar (Zone 1 HUD) — drains during the battle run */}
      {phase === "fighting" && (
        <div className="shrink-0 px-4 pt-1" data-testid="pvp-arena-enemy-hp">
          <div className="h-2 rounded-full bg-black/60 border border-white/10 overflow-hidden">
            <div className="h-full rounded-full" style={{ width: `${hpW}%`, background: "linear-gradient(90deg,#ff3b4e,#ff8a4e)", transition: "width 4.8s linear" }} />
          </div>
        </div>
      )}

      {/* Arena field on the canyon road backdrop */}
      <div className="relative flex-1 min-h-0 mx-3 my-2 rounded-xl overflow-hidden border border-white/10 flex flex-col justify-between py-3"
        style={{ backgroundImage: `url(${BACKEND}/assets/game/road_shooter_bg.png)`, backgroundSize: "cover", backgroundPosition: "center" }}>
        {phase === "fighting" ? (
          <ArenaBattleCanvas sprites={{ hero: img(me.heroes[0]?.image_path), troop: img(me.heroes[1]?.image_path) }}
            backdrop={`${BACKEND}/assets/game/road_shooter_bg.png`} durationMs={5000} onDone={resolveFight} />
        ) : (
          <>
            <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(255,59,78,0.18), rgba(0,0,0,0.32) 45%, rgba(57,255,122,0.18))" }} />
            <div className="relative"><Pads heroes={opp.heroes} color="#ff3b4e" testPrefix="pvp-arena-enemy" /></div>
            <div className="relative"><Pads heroes={me.heroes} color="#39ff7a" testPrefix="pvp-arena-mine" /></div>
          </>
        )}
      </div>

      {/* Action bar */}
      <div className="shrink-0 px-3 pb-4 flex items-center gap-2">
        <button onClick={load} disabled={phase === "fighting"} data-testid="pvp-arena-new-opponent" className="w-11 h-11 grid place-items-center rounded-full border-2 border-white/20 text-white/80 active:scale-95 disabled:opacity-40"><RefreshCw size={16} /></button>
        <button onClick={startFight} disabled={phase === "fighting"} data-testid="pvp-arena-battle" className="flex-1 flex items-center justify-center gap-2 py-3.5 rounded-full font-mono text-xs font-black uppercase tracking-widest border-2 border-black text-black bg-[#ff3b4e] shadow-[0_0_20px_rgba(255,59,78,0.4)] active:translate-y-[2px] disabled:opacity-60">
          {phase === "fighting" ? <Loader2 size={16} className="animate-spin" /> : <Swords size={16} />} {phase === "fighting" ? "Fighting…" : "Battle"}
        </button>
        <button onClick={() => setDetails(true)} disabled={phase === "fighting"} data-testid="pvp-arena-details" className="w-11 h-11 grid place-items-center rounded-full border-2 border-white/20 text-white/80 active:scale-95 disabled:opacity-40"><Info size={16} /></button>
      </div>

      {/* Result overlay */}
      {result && (
        <div className="absolute inset-0 z-30 grid place-items-center bg-black/80 px-6" data-testid="pvp-arena-result" onClick={() => setResult(null)}>
          <div className="w-full max-w-xs text-center" onClick={(e) => e.stopPropagation()}>
            <p className="font-mono text-4xl font-black uppercase tracking-widest" style={{ color: result.win ? "#39ff7a" : "#ff3b4e", textShadow: `0 0 20px ${result.win ? "#39ff7a" : "#ff3b4e"}` }} data-testid="pvp-arena-result-verdict">{result.win ? "Victory" : "Defeat"}</p>
            <div className="mt-3 flex items-center justify-center gap-3 font-mono text-sm font-black">
              <span className="text-[#39ff7a]">{fmt(result.my_roll)}</span>
              <span className="text-white/40">vs</span>
              <span className="text-[#ff3b4e]">{fmt(result.opp_roll)}</span>
            </div>
            {result.win && (
              <div className="mt-3 flex items-center justify-center gap-4 font-mono text-[11px] font-black text-[#f5c542]" data-testid="pvp-arena-rewards">
                <span>+{fmt(result.rewards.titanium)} Titanium</span>
                <span className="flex items-center gap-1"><Trophy size={12} /> +{result.rewards.trophies}</span>
              </div>
            )}
            <div className="mt-2 font-mono text-[9px] text-white/50">Trophies {result.trophies_total} · {result.record.wins}W / {result.record.losses}L</div>
            <button onClick={load} data-testid="pvp-arena-fight-again" className="mt-5 w-full py-3 rounded-full font-mono text-[11px] font-black uppercase tracking-widest border-2 border-black text-black bg-[#39ff7a]">Fight New Opponent</button>
            <button onClick={onBack} className="mt-2 w-full font-mono text-[9px] uppercase tracking-widest text-white/50">Back to Base</button>
          </div>
        </div>
      )}

      {/* Details modal */}
      {details && (
        <div className="absolute inset-0 z-30 grid place-items-center bg-black/80 px-5" data-testid="pvp-arena-details-modal" onClick={() => setDetails(false)}>
          <div className="w-full max-w-sm bg-[#06131a] border-2 border-white/15 rounded-xl p-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono text-[11px] font-black uppercase tracking-widest text-white">Matchup Details</p>
              <button onClick={() => setDetails(false)} className="w-7 h-7 grid place-items-center border border-white/20 rounded-full text-white"><X size={13} /></button>
            </div>
            <div className="grid grid-cols-2 gap-3 text-left">
              <div>
                <p className="font-mono text-[9px] font-black text-[#39ff7a] mb-1">YOU · {fmt(me.power)}</p>
                {me.synergy?.fb > 0 && <p className="font-mono text-[8px] text-white/70">Faction +{Math.round(me.synergy.fb * 100)}%</p>}
                {me.synergy?.balanced && <p className="font-mono text-[8px] text-white/70">Balanced +10% Crit</p>}
                {me.heroes.map((h) => <p key={h.card_id} className="font-mono text-[8px] text-white/60 truncate">{h.name} · {fmt(h.power_score)}</p>)}
              </div>
              <div>
                <p className="font-mono text-[9px] font-black text-[#ff3b4e] mb-1">{opp.name} · {fmt(opp.power)}</p>
                {opp.heroes.map((h) => <p key={h.card_id} className="font-mono text-[8px] text-white/60 truncate">{h.name} · {fmt(h.power_score)}</p>)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
